import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useAuth } from "../../../../../auth/AuthContext";
import { useToast } from "../../../../../hooks/use-toast";
import { documentApprovalService } from "../services/document-approval-service";
import { DocumentForApproval } from "../types";

export const documentApprovalKeys = {
  all: ["document-approval"] as const,
  list: (fundName: string) =>
    [...documentApprovalKeys.all, "list", fundName] as const,
};

interface UseDocumentApprovalProps {
  fundName: string;
  enabled?: boolean;
}

export function useDocumentApproval({
  fundName,
  enabled = true,
}: UseDocumentApprovalProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [selectedDocument, setSelectedDocument] =
    useState<DocumentForApproval | null>(null);

  const {
    data: documentsData,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: documentApprovalKeys.list(fundName),
    queryFn: async () => {
      if (!fundName) return [];
      const response = await documentApprovalService.getDocumentsForApproval({
        dbName: fundName,
      });

      console.log(response, "response documents for approval");
      return response.data || [];
    },
    enabled: enabled && !!fundName,
    staleTime: 1000 * 60 * 2,
  });

  const approveOrRejectDocument = useMutation({
    mutationFn: async (document: DocumentForApproval) => {
      if (!user) throw new Error("User not authenticated");

      return documentApprovalService.approveDocument({
        dbName: fundName,
        ops: document.ops || "",
        personType: document.personType === "jurídica" ? "jurídica" : "física",
        cnpj: document.cnpj || "",
        cpf: document.cpf || "",
        franchise: document.franchise || "",
        tipoTitularidadePix: document.cnpj ? "cnpj" : "cpf",
        pixKey: document.pixKey || "",
        pixKeyType:
          (document.pixKeyType as
            | "TELEFONE"
            | "EMAIL"
            | "CPF"
            | "CNPJ"
            | "CHAVE_ALEATORIA") || "CPF",
        fullName: document.fullName || document.nome_completo || "",
        phone: document.phone || document.telefone || "",
        birthday: document.birthday || document.data_nascimento || "",
        email: document.email || "",
        cep: document.cep || "",
        uf: document.uf || "",
        city: document.city || "",
        neighborhood: document.neighborhood || "",
        address: document.address || "",
        addressNumber: document.addressNumber || "",
        lgpdAgreeId: document.lgpdAgreeId || "",
        lgpdAgreeBucket: document.lgpdAgreeBucket || "",
        lgpdAgreeKeyname: document.lgpdAgreeKeyname || "",
        id: document.id,
        signedUrlCNH: document.signedUrlCNH || "",
        signedUrlSelfie: document.signedUrlSelfie || "",
        serviceCity: document.serviceCity === "tomador" ? "tomador" : "emissor",
        // Repassados sem fallback para "": gestorCadastroDocApproval decide pela
        // presença da key, e string vazia conta como ausente lá. Como axios serializa
        // JSON, undefined some do body e o backend lê corretamente como não informado.
        passwordNfse: document.passwordNfse,
        nfseEmitterType: document.nfseEmitterType,
        action: "approve",
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: documentApprovalKeys.list(fundName),
      });
      refetch();
    },
    onError: (error) => {
      toast({
        title: "Erro ao aprovar documento",
        description: error.message || "Ocorreu um erro ao aprovar o documento.",
        variant: "destructive",
      });
    },
  });

  const rejectMutation = useMutation({
    mutationFn: async (document: DocumentForApproval) => {
      if (!user) throw new Error("User not authenticated");

      return documentApprovalService.approveDocument({
        dbName: fundName,
        ops: document.ops || "",
        personType: document.personType === "jurídica" ? "jurídica" : "física",
        cnpj: document.cnpj || "",
        cpf: document.cpf || "",
        franchise: document.franchise || "",
        tipoTitularidadePix: document.cnpj ? "cnpj" : "cpf",
        pixKey: document.pixKey || "",
        pixKeyType:
          (document.pixKeyType as
            | "TELEFONE"
            | "EMAIL"
            | "CPF"
            | "CNPJ"
            | "CHAVE_ALEATORIA") || "CPF",
        fullName: document.fullName || document.nome_completo || "",
        phone: document.phone || document.telefone || "",
        birthday: document.birthday || document.data_nascimento || "",
        email: document.email || "",
        cep: document.cep || "",
        uf: document.uf || "",
        city: document.city || "",
        neighborhood: document.neighborhood || "",
        address: document.address || "",
        addressNumber: document.addressNumber || "",
        lgpdAgreeId: document.lgpdAgreeId || "",
        lgpdAgreeBucket: document.lgpdAgreeBucket || "",
        lgpdAgreeKeyname: document.lgpdAgreeKeyname || "",
        id: document.id,
        signedUrlCNH: document.signedUrlCNH || "",
        signedUrlSelfie: document.signedUrlSelfie || "",
        serviceCity: document.serviceCity === "tomador" ? "tomador" : "emissor",
        action: "reprove",
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: documentApprovalKeys.list(fundName),
      });
      refetch();
    },
  });

  const sendToAzumiMutation = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error("User not authenticated");

      return documentApprovalService.sendToAzumi({ dbName: fundName });
    },
  });

  return {
    documents: documentsData || [],
    isLoading,
    selectedDocument,
    setSelectedDocument,
    approveOrRejectDocument: approveOrRejectDocument.mutate,
    rejectDocument: rejectMutation.mutate,
    sendToAzumi: sendToAzumiMutation.mutate,
    isLoadingAction: approveOrRejectDocument.isPending,
    isRejecting: rejectMutation.isPending,
    isSendingToAzumi: sendToAzumiMutation.isPending,
    refetch,
  };
}
